import { useEffect, useRef } from 'react';

// Cursor-aware insertion for a controlled <input> / <textarea>. Toolbar +
// slug-picker actions compute the next value and the caret to restore; because
// the field is controlled, the caret is re-applied in an effect after React
// commits the new value.

type SelectionBuild = (selected: string) => {
  text: string;
  // Caret offsets RELATIVE to the insertion start. Default: end of inserted text.
  selectStart?: number;
  selectEnd?: number;
};

export function useTextInsertion<T extends HTMLInputElement | HTMLTextAreaElement>(
  value: string,
  onChange: (next: string) => void,
) {
  const ref = useRef<T>(null);
  const pending = useRef<[number, number] | null>(null);

  useEffect(() => {
    if (pending.current && ref.current) {
      const [s, e] = pending.current;
      ref.current.focus();
      ref.current.setSelectionRange(s, e);
      pending.current = null;
    }
  });

  const caretRange = (): [number, number] => {
    const el = ref.current;
    if (!el) return [value.length, value.length];
    return [el.selectionStart ?? value.length, el.selectionEnd ?? value.length];
  };

  const replaceSelection = (build: SelectionBuild) => {
    const [s, e] = caretRange();
    const selected = value.slice(s, e);
    const { text, selectStart, selectEnd } = build(selected);
    onChange(value.slice(0, s) + text + value.slice(e));
    const cs = s + (selectStart ?? text.length);
    const ce = s + (selectEnd ?? selectStart ?? text.length);
    pending.current = [cs, ce];
  };

  const insertAtCursor = (text: string) =>
    replaceSelection(() => ({ text, selectStart: text.length }));

  const wrapSelection = (before: string, after: string) =>
    replaceSelection((sel) => ({
      text: before + sel + after,
      selectStart: before.length,
      selectEnd: before.length + sel.length,
    }));

  // Prefix every line touched by the current selection (bullet / numbered list).
  const prefixLines = (makePrefix: (index: number) => string) => {
    const [s, e] = caretRange();
    const lineStart = value.lastIndexOf('\n', s - 1) + 1;
    const nl = value.indexOf('\n', e);
    const lineEnd = nl === -1 ? value.length : nl;
    const block = value.slice(lineStart, lineEnd);
    const transformed = block
      .split('\n')
      .map((line, i) => makePrefix(i) + line)
      .join('\n');
    onChange(value.slice(0, lineStart) + transformed + value.slice(lineEnd));
    pending.current = [lineStart, lineStart + transformed.length];
  };

  return { ref, insertAtCursor, wrapSelection, prefixLines, replaceSelection };
}
